// 📄 pages/Proyectos/components/CasoPruebaBibliotecaModal.tsx
// "Agregar desde biblioteca" — clona escenarios sugeridos (catálogo global
// admin, ver catalogoCasosPruebaService.ts) al proyecto actual, en vez de
// escribir el mismo caso típico de cero cada vez (idea #6 de integración).
import React, { useEffect, useMemo, useState } from 'react';
import { X, RefreshCw, FlaskConical, Check } from 'lucide-react';
import { EscenarioPrueba, getEscenariosPrueba } from '../../../services/catalogoCasosPruebaService.ts';
import { createCasoPrueba } from '../../../services/casoPruebaService.ts';
import { SOLUCIONES } from '../solucionesConfig.ts';
import { canalesDe } from './CasoPruebaFormModal.tsx';

interface CasoPruebaBibliotecaModalProps {
  projectId: string;
  solucionesActivas: string[];
  creadoPorUid: string;
  creadoPorNombre: string;
  onClose: () => void;
  onAgregados: () => void;
}

export default function CasoPruebaBibliotecaModal({
  projectId, solucionesActivas, creadoPorUid, creadoPorNombre, onClose, onAgregados
}: CasoPruebaBibliotecaModalProps) {
  const [escenarios, setEscenarios] = useState<EscenarioPrueba[] | null>(null);
  const [filtroSolucion, setFiltroSolucion] = useState(solucionesActivas[0] || 'commerce');
  const [filtroCanal, setFiltroCanal] = useState('todos');
  const [seleccionados, setSeleccionados] = useState<Set<string>>(new Set());
  const [agregando, setAgregando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getEscenariosPrueba().then(setEscenarios).catch((err) => {
      console.error('Error cargando la biblioteca de escenarios:', err);
      setError('No se pudo cargar la biblioteca de escenarios sugeridos.');
    });
  }, []);

  const opcionesCanal = useMemo(() => canalesDe(filtroSolucion), [filtroSolucion]);

  const filtrados = useMemo(() => {
    if (!escenarios) return [];
    return escenarios.filter((e) => {
      if (e.solucion !== filtroSolucion) return false;
      if (filtroCanal !== 'todos' && (e.canalId || '') !== filtroCanal) return false;
      return true;
    });
  }, [escenarios, filtroSolucion, filtroCanal]);

  const toggle = (id: string) => {
    setSeleccionados((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleAgregar = async () => {
    if (!escenarios || seleccionados.size === 0) return;
    setAgregando(true);
    setError(null);
    try {
      const elegidos = escenarios.filter((e) => seleccionados.has(e.id));
      await Promise.all(elegidos.map((e) => createCasoPrueba(
        projectId,
        { nombre: e.nombre, solucionRelacionada: e.solucion, canalId: e.canalId, canalNombre: e.canalNombre, campos: e.campos, detalles: e.detalles },
        [],
        creadoPorUid,
        creadoPorNombre
      )));
      onAgregados();
    } catch (err: any) {
      setError(err.message || 'Error al agregar los escenarios seleccionados.');
      setAgregando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-xl shadow-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-white/10 flex-shrink-0">
          <h3 className="text-lg font-medium text-white flex items-center gap-2"><FlaskConical className="w-5 h-5 text-blue-400" /> Agregar desde biblioteca</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>}

          <div className="flex items-center gap-3 flex-wrap">
            <select
              value={filtroSolucion}
              onChange={(e) => { setFiltroSolucion(e.target.value); setFiltroCanal('todos'); }}
              className="bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-xs text-white focus:outline-none"
            >
              {solucionesActivas.map((s) => <option key={s} value={s}>{SOLUCIONES[s]?.label || s}</option>)}
            </select>
            {opcionesCanal.length > 0 && (
              <select
                value={filtroCanal}
                onChange={(e) => setFiltroCanal(e.target.value)}
                className="bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-xs text-white focus:outline-none"
              >
                <option value="todos">Todos los canales</option>
                {opcionesCanal.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            )}
          </div>

          {escenarios === null ? (
            <div className="p-8 text-center text-gray-400 flex flex-col items-center gap-2">
              <RefreshCw className="w-5 h-5 animate-spin text-blue-500" />
            </div>
          ) : filtrados.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">No hay escenarios sugeridos para este filtro todavía.</p>
          ) : (
            <div className="space-y-2">
              {filtrados.map((e) => {
                const elegido = seleccionados.has(e.id);
                return (
                  <button
                    type="button"
                    key={e.id}
                    onClick={() => toggle(e.id)}
                    className={`w-full text-left flex items-start gap-3 p-3 rounded-xl border transition-all ${
                      elegido ? 'bg-blue-500/10 border-blue-500' : 'bg-white/5 border-white/10 hover:bg-white/10'
                    }`}
                  >
                    <div className={`mt-0.5 w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border ${elegido ? 'bg-blue-500 border-blue-500' : 'border-gray-500'}`}>
                      {elegido && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-gray-200">{e.nombre}</p>
                      {e.canalNombre && <p className="text-xs text-gray-500 mt-0.5">{e.canalNombre}</p>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 p-5 border-t border-white/10 flex-shrink-0">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white">Cancelar</button>
          <button
            onClick={handleAgregar}
            disabled={agregando || seleccionados.size === 0}
            className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all"
          >
            {agregando ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />}
            Agregar {seleccionados.size > 0 ? `(${seleccionados.size})` : ''}
          </button>
        </div>
      </div>
    </div>
  );
}
